import { Tag, X, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { TagType } from '../../redux/Slices/tags';
import { useCreateTagMutation } from '../../services/tagsApi';
import { cn } from '../../utils/tailwindMerge';

// Types
export type Test = {
  id: string;
  assignedStudentIds: string[];
  companyId: string;
  testName: string;
  organizerId: string;
  status: 'Active' | 'Upcoming' | 'Completed';
  tagId: string;
  createdDate: string;
  testStartTime: string;
  testEndTime: string;
  tenantId: string;
};

export type Student = {
  studentId: string;
  studentName: string;
  emailId: string;
  mobileNumber: string;
  branch: string;
};

export type TagCreationDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any, tagId?: string) => void;
  existingTags: TagType[];
  editingTag: TagType | null;
  associatedStudents: Student[];
};

const TagCreationDialog = ({
  isOpen,
  onClose,
  onSave,
  existingTags,
  editingTag,
  associatedStudents = []
}: TagCreationDialogProps) => {
  const [tagName, setTagName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [createTag] = useCreateTagMutation();

  useEffect(() => {
    if (editingTag) {
      setTagName(editingTag.tagName);
      setDescription(editingTag.description);
      //   setSelectedStudentIds(editingTag.studentIds);
    } else {
      setTagName('');
      setDescription('');
      setSelectedStudentIds([]);
    }
    setSearchQuery('');
    setError('');
  }, [editingTag, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const normalizeTagName = (name: string) =>
    name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isDuplicateTag = (name: string) => {
    const normalized = normalizeTagName(name);
    return existingTags.some((tag: TagType) => {
      if (editingTag && tag.id === editingTag.id) return false;
      return normalizeTagName(tag.tagName) === normalized;
    });
  };

  const handleSubmit = async () => {
    try {
      setError('');
      const trimmed = tagName.trim();
      if (!trimmed) {
        setError('Batch name is required');
        return;
      }
      if (isDuplicateTag(trimmed)) {
        setError('A batch with this name already exists');
        return;
      }
      await createTag({
        tagName: trimmed,
        description: description.trim(),
        studentIds: selectedStudentIds
      }).unwrap();
      onSave({
        tagName: trimmed,
        description: description.trim(),
        studentIds: selectedStudentIds
      });
      setTagName('');
      setDescription('');
      setSelectedStudentIds([]);
      setError('');
      toast.success(`Tag ${editingTag ? 'updated' : 'created'} successfully`);
      onClose();
    } catch (err) {
      toast.error(
        (err as any)?.data?.message ?? 'Failed to create tag. Please try again.'
      );
    }
  };

  const handleStudentToggle = (id: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const filteredStudents = associatedStudents.filter(
    (s: Student) =>
      s.studentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.emailId?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedStudents = associatedStudents.filter((s: Student) =>
    selectedStudentIds.includes(s.studentId)
  );

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-8 shadow-2xl'>
        <div className='mb-6 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100'>
              <Tag className='h-5 w-5 text-blue-600' />
            </div>
            <h2 className='text-2xl font-bold text-gray-900'>
              {editingTag ? 'Edit Batch' : 'Create New Batch'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className='rounded-lg p-2 text-gray-400 hover:bg-gray-100'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        <div className='mb-5'>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Batch Name <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            value={tagName}
            onChange={e => {
              setTagName(e.target.value);
              setError('');
            }}
            placeholder='Enter batch name'
            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
          />
          {error && <p className='mt-1.5 text-sm text-red-600'>{error}</p>}
        </div>

        <div className='mb-5'>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Description
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder='Enter description (optional)'
            rows={3}
            className='w-full resize-none rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
          />
        </div>

        <div className='mb-6'>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Assign Students <span className='text-red-500'>*</span>
          </label>
          {selectedStudents?.length > 0 && (
            <div className='mb-3 flex flex-wrap gap-2'>
              {selectedStudents.map(s => (
                <div
                  key={s?.studentId}
                  className='flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm'
                >
                  <span className='font-medium text-blue-900'>
                    {s?.studentName}
                  </span>
                  <button
                    onClick={() => handleStudentToggle(s?.studentId)}
                    className='text-blue-600 hover:text-blue-800'
                  >
                    <X className='h-3.5 w-3.5' />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className='relative' ref={dropdownRef}>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
            <input
              type='text'
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              placeholder='Search students...'
              className='w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100'
            />
            {showDropdown && (
              <div className='absolute z-10 mt-2 max-h-64 w-full overflow-y-auto rounded-lg border bg-white shadow-lg'>
                {filteredStudents?.length === 0 ? (
                  <div className='p-4 text-center text-sm text-gray-500'>
                    No students found
                  </div>
                ) : (
                  filteredStudents?.map(s => (
                    <div
                      key={s?.studentId}
                      onClick={() => handleStudentToggle(s?.studentId)}
                      className='cursor-pointer border-b p-3 last:border-b-0 hover:bg-blue-50'
                    >
                      <div className='flex items-center gap-3'>
                        <input
                          type='checkbox'
                          checked={selectedStudentIds.includes(s?.studentId)}
                          onChange={() => {
                            // Handled by parent div click
                          }}
                          className='h-4 w-4 rounded text-blue-600'
                        />
                        <div>
                          <div className='font-medium text-gray-900'>
                            {s?.studentName}
                          </div>
                          <div className='text-xs text-gray-500'>
                            {s?.emailId}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className='flex justify-end gap-3'>
          <button
            onClick={onClose}
            className='rounded-lg border border-gray-300 px-6 py-2.5 font-semibold text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            disabled={tagName.trim() === '' || selectedStudents?.length === 0}
            onClick={handleSubmit}
            className={cn(
              tagName?.trim() === '' || selectedStudents?.length === 0
                ? 'cursor-not-allowed rounded-lg bg-gray-400 px-6 py-2.5 font-semibold text-white'
                : 'rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white hover:bg-blue-700'
            )}
          >
            {editingTag ? 'Update Batch' : 'Create Batch'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagCreationDialog;
